import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, addDays } from 'date-fns';
import {
  CheckCircle2, ArrowRight, X, ExternalLink, SkipForward, Undo2, RotateCcw,
  CalendarPlus, Send, Pencil, Lock, Unlock, Plus, BellRing, Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { NudgeClientDialog, nudgeCooldownInfo } from '@/components/NudgeClientDialog';
import ProjectComposeEmail from '@/components/ProjectComposeEmail';
import '@/styles/kmj-step.css';
import {
  buildStepUrl, stepErrorToast, getUnmetDependencies, getSmartActions, completeStepRequest,
} from './stepActions';

/**
 * StepDetailPanel — DP41 commit 1.
 *
 * LIFTED VERBATIM out of JourneySteps.js (`renderDetailPanel` plus the five
 * dialogs it triggers) so the panel can live on My Desk and on the project
 * page, under either viewer, without a second copy of it existing anywhere.
 * The markup is unchanged in this commit; the restyle is commit 3.
 *
 * Selection stays with the caller: the panel asks to be closed (`onClose`) and
 * asks for its step to be patched (`onStepPatch`), exactly as the old closure
 * called `setSelectedStep`.
 */
export default function StepDetailPanel({
  project,
  step,
  allSteps = [],
  darkMode = false,
  compact = false,
  teamMembers = [],
  variant = 'project',
  stepUrlBuilder = null,
  onUpdate,
  onActivityRefresh = () => {},
  onClose = () => {},
  onStepPatch = () => {},
  // The strip's Enter key completes with whatever this checkbox says, so the
  // caller may own it. Left alone it behaves exactly as it did.
  cancelActivityLog: cancelActivityLogProp,
  onCancelActivityLogChange,
  // DP42 — B.9 numbers its steps from the card they belong to (B.9.1, B.9.2 …)
  // and keeps the panel open after a mutation, so pressing Mark Complete SHOWS
  // that it worked instead of closing the thing you were reading.
  stepNumber = null,
  closeOnMutate = true,
}) {
  const navigate = useNavigate();
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [skipLogActivity, setSkipLogActivity] = useState(true);
  const [ownCancelActivityLog, setOwnCancelActivityLog] = useState(false);
  const cancelActivityLog = cancelActivityLogProp === undefined ? ownCancelActivityLog : cancelActivityLogProp;
  const setCancelActivityLog = onCancelActivityLogChange || setOwnCancelActivityLog;
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '09:00', assignee: '' });
  const [skipReason, setSkipReason] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', notes: '', is_critical: false });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [showNudgeDialog, setShowNudgeDialog] = useState(false);

  const stepUrl = (s) => buildStepUrl(project, s, stepUrlBuilder);
  const settle = () => { if (closeOnMutate) onClose(); };

  const handleCompleteStep = async (s, skipActivityLog = false) => {
    try {
      await completeStepRequest(stepUrl(s), skipActivityLog);
      toast.success('Step completed!');
      settle();
      if (onUpdate) onUpdate();
      onActivityRefresh();
    } catch (error) {
      stepErrorToast(error, 'Failed to complete step');
    }
  };

  const handleSkipStep = async () => {
    if (!step) return;
    try {
      await axios.put(stepUrl(step), {
        status: 'skipped',
        completed: false,
        skip_reason: skipReason.trim() || null,
        skip_activity_log: !skipLogActivity,
      });
      toast.success('Step marked not needed');
      setShowSkipDialog(false);
      setSkipReason('');
      settle();
      if (onUpdate) onUpdate();
      onActivityRefresh();
    } catch (error) {
      stepErrorToast(error, 'Failed to mark step not needed');
    }
  };

  const handleUndoStep = async () => {
    if (!step) return;
    const wasNotNeeded = step.status === 'skipped';
    try {
      await axios.put(stepUrl(step), {
        completed: false,
        status: 'pending',
        completed_at: null,
        completed_by: null,
        skip_reason: null,
      });
      toast.success(wasNotNeeded ? 'Step restored' : 'Step completion undone');
      setShowUndoDialog(false);
      settle();
      if (onUpdate) onUpdate();
      onActivityRefresh();
    } catch (error) {
      stepErrorToast(error, 'Failed to update step');
    }
  };

  const handleScheduleStep = async () => {
    if (!step || !scheduleForm.date) {
      toast.error('Please select a date');
      return;
    }
    try {
      const assignee = scheduleForm.assignee && scheduleForm.assignee !== 'unassigned' ? scheduleForm.assignee : null;
      await axios.put(stepUrl(step), {
        due_date: scheduleForm.date,
        assigned_contacts: assignee ? [assignee] : [],
      });
      toast.success('Step scheduled!');
      setShowScheduleDialog(false);
      settle();
      setScheduleForm({ date: '', time: '09:00', assignee: '' });
      if (onUpdate) onUpdate();
    } catch (error) {
      stepErrorToast(error, 'Failed to schedule step');
    }
  };

  const openEditDialog = () => {
    if (!step) return;
    setEditForm({
      name: step.name || '',
      description: step.description || '',
      notes: step.notes || '',
      is_critical: Boolean(step.is_critical),
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!step) return;
    if (!editForm.name.trim()) {
      toast.error('Step name is required');
      return;
    }
    try {
      await axios.put(stepUrl(step), { ...editForm, skip_activity_log: true });
      toast.success('Step updated');
      setShowEditDialog(false);
      onStepPatch({ ...editForm });
      if (onUpdate) onUpdate();
    } catch (error) {
      stepErrorToast(error, 'Failed to update step');
    }
  };

  const handleToggleLock = async () => {
    if (!step) return;
    const newLocked = !step.is_locked;
    try {
      await axios.put(stepUrl(step), { is_locked: newLocked });
      toast.success(newLocked ? 'Step locked' : 'Step unlocked');
      onStepPatch({ is_locked: newLocked });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update step lock');
    }
  };

  const handleAddChecklistItem = async () => {
    if (!step || !newChecklistItem.trim()) return;
    try {
      const res = await axios.post(`${stepUrl(step)}/checklist`, { text: newChecklistItem.trim() });
      onStepPatch({ checklist: [...(step.checklist || []), res.data.item] });
      setNewChecklistItem('');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to add checklist item');
    }
  };

  const handleToggleChecklistItem = async (itemId, completed) => {
    if (!step) return;
    try {
      await axios.put(`${stepUrl(step)}/checklist/${itemId}`, {}, { params: { completed } });
      onStepPatch({
        checklist: (step.checklist || []).map(i => (i.id === itemId ? { ...i, completed } : i)),
      });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update checklist item');
    }
  };

  const executeAction = (action) => {
    if (action.external) {
      window.open(action.external, '_blank');
    } else if (action.action === 'email') {
      setComposeEmailOpen(true);
    } else if (action.route) {
      navigate(action.route);
    }
  };

  if (!step) return null;

  const isCompleted = step.completed || step.status === 'completed';
  const isSkipped = step.status === 'skipped';
  const unmet = getUnmetDependencies(step, allSteps);
  const actions = getSmartActions(step, project, variant);

  return (
    <>
      <div className="kmj-step" data-testid="step-detail-panel" data-dark={darkMode ? 'true' : undefined}>
        {/* The title block replaces the blue bar: marker, name, phase and step
            number as the right-hand meta. Same three controls as before. */}
        <div className="kmj-step__block">
          <span className="kmj-step__series">{stepNumber || `${step.phaseIndex + 1}.${step.stepIndex + 1}`}</span>
          <h3 className="kmj-step__name">{step.name}</h3>
          {/* DP43 — the number is in the marker; the meta says the phase and
              nothing else. It was printing the step number a second time. */}
          <span className="kmj-step__meta">{step.phase.name}</span>
          <span className="kmj-step__acts">
            {variant !== 'onboarding' && (
              <>
                <button type="button" className="kmj-step__act" onClick={openEditDialog}
                        title="Edit step" data-testid="edit-step-btn">
                  <Pencil className="w-4 h-4" />
                </button>
                <button type="button" className="kmj-step__act" onClick={handleToggleLock}
                        title={step.is_locked ? 'Unlock step' : 'Lock step'} data-testid="toggle-step-lock-btn">
                  {step.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </button>
              </>
            )}
            <button type="button" className="kmj-step__act" onClick={onClose} title="Close">
              <X className="w-4 h-4" />
            </button>
          </span>
        </div>

        <div className="kmj-step__body">
          <div className="kmj-step__main">
            {step.definition && (
              <div>
                <div className="kmj-step__label">What to Do</div>
                <p className="kmj-step__text">{step.definition}</p>
              </div>
            )}

            {step.why_it_matters && (
              <div>
                <div className="kmj-step__label">Why It Matters</div>
                <p className="kmj-step__text">{step.why_it_matters}</p>
              </div>
            )}

            {step.deliverable_description && (
              <div className="kmj-step__deliv">
                <div className="kmj-step__label">Deliverable</div>
                <p className="kmj-step__text">{step.deliverable_description}</p>
              </div>
            )}

            {step.tips?.length > 0 && (
              <div>
                <div className="kmj-step__label">Tips</div>
                <ul className="kmj-step__tips">
                  {step.tips.map((tip, idx) => (
                    <li key={idx}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Advisory dependency hint — never blocks completion */}
            {unmet.length > 0 && (
              <div className="kmj-step__hint" data-testid="step-dependency-hint">
                Usually follows: {unmet.join(', ')}
              </div>
            )}

            {step.notes && (
              <div>
                <div className="kmj-step__label">Notes</div>
                <p className="kmj-step__text" data-testid="step-notes">{step.notes}</p>
              </div>
            )}

            {/* Checklist — project containers only */}
            {variant !== 'onboarding' && (
              <div data-testid="step-checklist">
                <div className="kmj-step__label">
                  Checklist
                  {step.checklist?.length > 0
                    ? ` · ${step.checklist.filter(c => c.completed).length}/${step.checklist.length}`
                    : ''}
                </div>
                <div className="kmj-step__check">
                  {(step.checklist || []).length > 0 ? (
                    (step.checklist || []).map(item => (
                      <div key={item.id} className="kmj-step__checkrow" data-done={item.completed ? 'true' : 'false'}>
                        <Checkbox
                          checked={Boolean(item.completed)}
                          onCheckedChange={(checked) => handleToggleChecklistItem(item.id, Boolean(checked))}
                          data-testid={`checklist-item-${item.id}`}
                        />
                        <span className="kmj-step__checktext">{item.text}</span>
                      </div>
                    ))
                  ) : (
                    <p className="kmj-step__note">No checklist items yet</p>
                  )}
                  <div className="kmj-step__addrow">
                    <input
                      className="kmj-step__input"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                      placeholder="Add checklist item..."
                      data-testid="add-checklist-input"
                    />
                    <button
                      type="button"
                      className="kmj-step__alt"
                      onClick={handleAddChecklistItem}
                      disabled={!newChecklistItem.trim()}
                      data-testid="add-checklist-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="kmj-step__side">
            <div className="kmj-step__panel">
              <div className="kmj-step__label">Status</div>

              {isCompleted ? (
                <div className="kmj-step__stack">
                  <span className="kmj-step__badge kmj-step__badge--done">Completed</span>
                  {step.completed_at && (
                    <p className="kmj-step__note">{format(parseISO(step.completed_at), 'MMM d, yyyy at h:mm a')}</p>
                  )}
                  {step.completed_by && (
                    <p className="kmj-step__note">By: {step.completed_by}</p>
                  )}
                  <button type="button" className="kmj-step__alt" onClick={() => setShowUndoDialog(true)}>
                    <Undo2 className="w-4 h-4" />
                    Undo Completion
                  </button>
                </div>
              ) : isSkipped ? (
                <div className="kmj-step__stack">
                  <span className="kmj-step__badge">Not needed</span>
                  {step.skip_reason && (
                    <p className="kmj-step__note" data-testid="not-needed-reason">Reason: {step.skip_reason}</p>
                  )}
                  <button type="button" className="kmj-step__alt" onClick={() => setShowUndoDialog(true)}>
                    <RotateCcw className="w-4 h-4" />
                    Restore Step
                  </button>
                </div>
              ) : step.is_locked ? (
                <div className="kmj-step__stack">
                  <span className="kmj-step__badge kmj-step__badge--wait">Locked</span>
                  <p className="kmj-step__note">This step is locked. Unlock it to make changes.</p>
                  <button type="button" className="kmj-step__alt" onClick={handleToggleLock} data-testid="unlock-step-btn">
                    <Unlock className="w-4 h-4" />
                    Unlock Step
                  </button>
                </div>
              ) : (
                <div className="kmj-step__stack">
                  <button
                    type="button"
                    className="kmj-step__go"
                    onClick={() => handleCompleteStep(step, cancelActivityLog)}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Mark Complete
                  </button>

                  <div className="kmj-step__altrow">
                    {variant !== 'onboarding' && (
                      <button
                        type="button"
                        className="kmj-step__alt"
                        onClick={() => {
                          setScheduleForm({
                            date: step.due_date || format(addDays(new Date(), 1), 'yyyy-MM-dd'),
                            time: '09:00',
                            assignee: '',
                          });
                          setShowScheduleDialog(true);
                        }}
                      >
                        <CalendarPlus className="w-4 h-4" />
                        Schedule
                      </button>
                    )}
                    <button
                      type="button"
                      className="kmj-step__alt"
                      onClick={() => { setSkipReason(''); setShowSkipDialog(true); }}
                      data-testid="not-needed-btn"
                    >
                      <SkipForward className="w-4 h-4" />
                      Not needed
                    </button>
                  </div>

                  {variant !== 'onboarding' && step.action_type === 'approval' && (() => {
                    const cd = nudgeCooldownInfo(step.last_nudged_at);
                    return (
                      <button
                        type="button"
                        className="kmj-step__alt"
                        title={cd.inCooldown ? `Nudged ${cd.hoursAgo}h ago` : 'Email the client a friendly reminder'}
                        disabled={cd.inCooldown}
                        onClick={() => setShowNudgeDialog(true)}
                        data-testid="nudge-client-btn"
                      >
                        <BellRing className="w-4 h-4" />
                        {cd.inCooldown ? `Nudged ${cd.hoursAgo}h ago` : 'Nudge client'}
                      </button>
                    );
                  })()}

                  {variant !== 'onboarding' && (
                    <div className="kmj-step__logrow">
                      <Checkbox
                        id="cancel-log"
                        checked={cancelActivityLog}
                        onCheckedChange={setCancelActivityLog}
                      />
                      <label htmlFor="cancel-log" className="kmj-step__loglabel">
                        Don&apos;t log to activity timeline
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="kmj-step__panel">
              <div className="kmj-step__label">Quick Actions</div>
              <div className="kmj-step__stack">
                {actions.map((action, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`kmj-step__qa${action.keymade ? ' kmj-step__qa--nav' : ''}`}
                    onClick={() => executeAction(action)}
                  >
                    {action.external ? (
                      <ExternalLink className="w-4 h-4" />
                    ) : action.action === 'email' ? (
                      <Send className="w-4 h-4" />
                    ) : (
                      <ArrowRight className="w-4 h-4" />
                    )}
                    {action.label}
                  </button>
                ))}
                {actions.length === 0 && (
                  <p className="kmj-step__note">No quick actions available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Step Dialog — per-project edits */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Step</DialogTitle>
            <DialogDescription>
              Changes apply to this project only. Templates are managed in Admin Settings.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
                data-testid="edit-step-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="mt-1"
                data-testid="edit-step-description-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Add any notes or decisions..."
                className="mt-1"
                data-testid="edit-step-notes-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit-is-critical"
                checked={editForm.is_critical}
                onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_critical: Boolean(checked) }))}
                data-testid="edit-step-critical-toggle"
              />
              <Label htmlFor="edit-is-critical" className="text-sm cursor-pointer">
                <Star className="w-4 h-4 inline mr-1" />
                Foundational step (for this project)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <button type="button" className="kmj-step__go" onClick={handleSaveEdit} data-testid="save-step-edit-btn">
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nudge Client Dialog (approval steps) */}
      <NudgeClientDialog
        open={showNudgeDialog}
        onOpenChange={setShowNudgeDialog}
        project={project}
        itemType="approval_step"
        itemId={step?.id}
        itemTitle={step?.name}
        darkMode={darkMode}
        onSent={(data) => {
          onStepPatch({
            last_nudged_at: data?.last_nudged_at || new Date().toISOString(),
            nudge_count: data?.nudge_count,
          });
          if (onUpdate) onUpdate();
          onActivityRefresh();
        }}
      />

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Step</DialogTitle>
            <DialogDescription>
              Set a due date and assign this step to a team member.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={scheduleForm.date}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>

            <div>
              <Label>Assign To</Label>
              <Select
                value={scheduleForm.assignee}
                onValueChange={(value) => setScheduleForm(prev => ({ ...prev, assignee: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <button type="button" className="kmj-step__go" onClick={handleScheduleStep}>
              <CalendarPlus className="w-4 h-4" />
              Schedule
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Not Needed Dialog */}
      <AlertDialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this step as not needed?</AlertDialogTitle>
            <AlertDialogDescription>
              The step will be set aside and won&apos;t count against progress. You can restore it later with one click.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {step?.is_critical && (
            <div className="kmj-step__hint" data-testid="foundational-step-warning">
              This is a foundational step: {step?.name}. Continue anyway?
            </div>
          )}

          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                placeholder="e.g. Client already provided this"
                className="mt-1"
                data-testid="not-needed-reason-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="log-skip"
                checked={skipLogActivity}
                onCheckedChange={setSkipLogActivity}
              />
              <Label htmlFor="log-skip" className="text-sm cursor-pointer">
                Log to activity timeline
              </Label>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSkipStep} data-testid="confirm-not-needed-btn">
              <SkipForward className="w-4 h-4 mr-2" />
              Mark Not Needed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Undo Dialog */}
      <AlertDialog open={showUndoDialog} onOpenChange={setShowUndoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {step?.status === 'skipped' ? 'Restore this step?' : 'Undo completion?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {step?.status === 'skipped'
                ? 'This not-needed step will be restored to pending status.'
                : 'This will mark the step as incomplete and remove the completion timestamp.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUndoStep}>
              <Undo2 className="w-4 h-4 mr-2" />
              {step?.status === 'skipped' ? 'Restore' : 'Undo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* In-app Compose Email modal (replaces `mailto:`). */}
      <ProjectComposeEmail
        open={composeEmailOpen}
        onOpenChange={setComposeEmailOpen}
        projectId={project?.id}
        projectName={project?.name}
        stepId={step?.id}
        stepName={step?.name}
        stepPhase={step?.phase?.name || step?.phase_name || ''}
        defaultTemplateKey={step?.default_email_template_key || ''}
        onSent={async ({ stepId: sId, markComplete }) => {
          if (markComplete && sId && step?.id === sId) {
            await handleCompleteStep(step, false);
          }
        }}
      />
    </>
  );
}
